import { CircularProgress } from '@civilgrid/ui/components';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@civilgrid/ui/Tabs';
import { Link, useParams } from '@tanstack/react-router';
import { isNil } from 'lodash';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { subscribeKey } from 'valtio/utils';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import Icon from '~/components2/Icon';
import { useOwnMapClicks } from '~/components2/Map/mapClickOwnership';
import { measureStatus } from '~/components2/Map/Widgets/Measure/MeasureSurface';
import { useGetProject } from '~/hooks/useGetProject';
import { useProjectIntelligence } from '~/hooks/useProjectIntelligence';
import { useTabControlledLayers } from '~/store2/tabLayerLocks';
import type { ProjectDetailsParams } from '..';
import { IntelligenceSectionTitle } from './common';
import { layersToSkipRender } from './common/config';
import { layerIdMap } from './layers/common';
import FeatureLayers from './layers/FeatureLayers';
import HoverLayer from './layers/HoverLayer';
import SelectedLayer from './layers/SelectedLayer';
import ProjectOverview from './ProjectOverview';
import store from './store';
import { Conflicts } from './tabs/conflicts';
import Infrastructure from './tabs/infrastructure';
import Jurisdiciton from './tabs/jurisdiction';
import RightOfWay from './tabs/right-of-way';

export function MapChildren() {
  // This tab uses left-click to select features, so claim map-click ownership (CG-670 sidebar defers).
  useOwnMapClicks();
  const { current: map } = MapGL.useMap();
  const { config, intelligenceFeatures } = useSnapshot(store) as typeof store;

  async function onClick(e: MapGL.MapMouseEvent) {
    if (measureStatus.isMeasuring === true) return;

    const findFeature = () => {
      const queriedFeatures = map?.queryRenderedFeatures(e.point);

      let foundFeature: GeoJSON.Feature | undefined;

      const intelFeatureIds = Object.keys(intelligenceFeatures);
      const feat = queriedFeatures?.find(
        (f) => f.layer?.source?.toString() && intelFeatureIds.includes(f.layer.source.toString()),
      );

      if (feat?.layer?.source?.toString()) {
        const featureId = feat.layer.source?.toString();
        const layerId = intelligenceFeatures[featureId]?.layerId;

        if (layerId) {
          const layer = config[layerId];
          if (layer) {
            const match = layer[featureId];
            if (match) {
              foundFeature = match;
            }
          }
        }
      }

      return foundFeature;
    };

    if (map) {
      const foundFeature = findFeature();

      if (foundFeature) {
        store.setSelectedFeature(foundFeature);
        capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2FeatureSelected, {
          project_id: store.project?.id,
          organization_id: store.project?.organization,
          accessed_from: 'map',
          feature_id: foundFeature.id,
        });
        // settimeout needed here so function doesn't "return" before map view change.
        setTimeout(() => store.zoomToFeature(foundFeature), 0);
        return;
      }
    }

    store.resetSelectedFeature();
  }

  function onKeyUp(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      if (store.selectedFeature) {
        store.resetSelectedFeature();
      }
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    store.setCurrentMap(map);
    map?.on('click', onClick);

    window.addEventListener('keyup', onKeyUp);

    const unsubscribe = subscribeKey(store, 'activeSection', () => {
      store.resetSelectedFeature();
      store.resetHoveredFeature();
    });

    return () => {
      map?.off('click', onClick);
      window.removeEventListener('keyup', onKeyUp);
      store.resetCurrentMap();
      unsubscribe();
    };
  }, [map, intelligenceFeatures, config]);

  return (
    <>
      <FeatureLayers />
      <HoverLayer />
      <SelectedLayer />
    </>
  );
}

const TabValueIntelligenceSectionTitleMap: { [key: string]: IntelligenceSectionTitle } = {
  conflicts: IntelligenceSectionTitle.NearbyProjects,
  'right-of-way': IntelligenceSectionTitle.RightOfWay,
  jurisdictions: IntelligenceSectionTitle.JurisdictionalIntersections,
  infrastructure: IntelligenceSectionTitle.PhysicalInfrastructure,
};

export function TabDetails() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;

  const [isMissingDates, setIsMissingDates] = useState(false);

  const { data: project, isLoading, isError } = useGetProject({ projectId });

  const [tabSwitchCount, setTabSwitchCount] = useState<Record<IntelligenceSectionTitle, number>>({
    [IntelligenceSectionTitle.None]: 0,
    [IntelligenceSectionTitle.NearbyProjects]: 0,
    [IntelligenceSectionTitle.RightOfWay]: 0,
    [IntelligenceSectionTitle.JurisdictionalIntersections]: 0,
    [IntelligenceSectionTitle.PhysicalInfrastructure]: 0,
  });

  useEffect(() => {
    setIsMissingDates(false);
    if (!isNil(project)) {
      store.setProject(project);
      if (!project.constructionStartDate || !project.constructionEndDate) {
        setIsMissingDates(true);
      }
    }
  }, [project]);

  const { activeSection } = useSnapshot(store) as typeof store;

  // The active section's ids are forced on ADDITIVELY (the user's other layers stay on,
  // same behavior as the Land Rights / Zoning tabs) and tab-locked, so the picker shows
  // them checked + disabled instead of fighting the tab. A section switch or leaving the
  // tab returns the previous section's ids to picker-controlled state.
  const forcedSectionLayerIds = useMemo(
    () => (layerIdMap[activeSection] || []).filter((id) => !layersToSkipRender.includes(id)),
    [activeSection],
  );
  useTabControlledLayers(forcedSectionLayerIds);

  const {
    data: projectIntelligence,
    isLoading: isProjectIntelligenceLoading,
    isError: isProjectIntelligenceError,
    error: projectIntelligenceError,
  } = useProjectIntelligence(
    { projectId },
    {
      refetchInterval: (query) => {
        const computeStatus = query.state.data?.intelligenceSummary?.computeStatus;
        if (computeStatus === 'processing') {
          return 5 * 1000;
        }
        return false;
      },
    },
  );

  useEffect(() => {
    if (!isNil(projectIntelligence)) {
      store.setProjectIntelligence(projectIntelligence);
    }
  }, [projectIntelligence]);

  // Fires once per failure — keyed on the boolean so a retry that fails again doesn't re-fire.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally excludes projectIntelligenceError, whose identity changes on every retry
  useEffect(() => {
    if (!isProjectIntelligenceError || !project?.organization) return;

    console.error('Project intelligence fetch failed', { projectId });
    capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2FetchFailed, {
      project_id: projectId,
      organization_id: project?.organization,
      error_message: projectIntelligenceError instanceof Error ? projectIntelligenceError.message : undefined,
    });
  }, [isProjectIntelligenceError, project?.organization, projectId]);

  const projectIntelligenceSummary = projectIntelligence?.intelligenceSummary;

  useEffect(() => {
    return () => {
      store.reset();
      setIsMissingDates(false);
    };
  }, []);

  const tabSwitchRafIds = useRef<{ outer: number; inner: number | null }>({ outer: -1, inner: null });

  useEffect(() => {
    return () => {
      cancelAnimationFrame(tabSwitchRafIds.current.outer);
      if (tabSwitchRafIds.current.inner !== null) {
        cancelAnimationFrame(tabSwitchRafIds.current.inner);
      }
    };
  }, []);

  if (isLoading || isProjectIntelligenceLoading) {
    return <CircularProgress indeterminate />;
  }

  if (isMissingDates) {
    return (
      <div className="text-sm">
        Please select construction start and end dates to access project intelligence. You can do this in the{' '}
        <Link
          className="text-blue-50 underline hover:text-blue-60"
          to="/projects/$projectId/$tabId"
          params={{ projectId, tabId: 'details' }}
          data-testid="intelligence-project-info-link"
          onClick={() =>
            capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2ProjectInfoLinkClicked, {
              project_id: project?.id,
              organization_id: project?.organization,
            })
          }
        >
          Project Info
        </Link>{' '}
        tab.
      </div>
    );
  }

  if (isError || isProjectIntelligenceError) {
    return (
      <div className="flex gap-2 text-sm" data-testid="intelligence-summary-error">
        <Icon icon="warning" className="self-center" />
        <span className="text-sm">Failed to fetch project intelligence.</span>
      </div>
    );
  }

  const overview = project && <ProjectOverview project={project} summary={projectIntelligenceSummary} />;

  if (projectIntelligenceSummary?.computeStatus === 'error') {
    return (
      <>
        {overview}
        <div className="flex gap-2 text-sm" data-testid="intelligence-summary-error">
          <Icon icon="warning" className="self-center" />
          <span>Intelligence failed to refresh for this project. We're looking into it.</span>
        </div>
      </>
    );
  }

  if (projectIntelligenceSummary?.computeStatus === 'processing') {
    return (
      <>
        {overview}
        <div className="flex gap-2 text-sm items-center">
          <CircularProgress indeterminate />
          <span>Project intelligence is still processing. Please try again later.</span>
        </div>
      </>
    );
  }

  return (
    <Fragment key={project?.id}>
      {overview}
      <Tabs
        defaultValue="conflicts"
        onValueChange={(v) => {
          const switchStartedAt = performance.now();
          const selectedTab = TabValueIntelligenceSectionTitleMap[v];
          store.activeSection = selectedTab;
          capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2TabSwitched, {
            project_id: project?.id,
            organization_id: project?.organization,
            tab_name: v,
            section: selectedTab,
          });
          // Double rAF: the first fires once React has committed the new tab's DOM,
          // the second once the browser has actually painted it.
          cancelAnimationFrame(tabSwitchRafIds.current.outer);
          if (tabSwitchRafIds.current.inner !== null) {
            cancelAnimationFrame(tabSwitchRafIds.current.inner);
          }
          tabSwitchRafIds.current.inner = null;
          tabSwitchRafIds.current.outer = requestAnimationFrame(() => {
            tabSwitchRafIds.current.inner = requestAnimationFrame(() => {
              capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2TabSwitchRendered, {
                project_id: project?.id,
                organization_id: project?.organization,
                tab_name: v,
                section: selectedTab,
                tab_switch_duration_ms: Math.round(performance.now() - switchStartedAt),
                tab_switch_count: tabSwitchCount[selectedTab],
              });

              setTabSwitchCount((prev) => ({
                ...prev,
                [selectedTab]: prev[selectedTab] + 1,
              }));
            });
          });
        }}
      >
        <TabsList className="gap-0 mt-3">
          <TabsTrigger
            value="conflicts"
            className="px-3 text-xs font-normal data-[state=active]:font-medium"
            data-testid="intelligence-tab-conflicts"
          >
            Conflicts
          </TabsTrigger>
          <TabsTrigger
            value="right-of-way"
            className="px-3 text-xs font-normal data-[state=active]:font-medium"
            data-testid="intelligence-tab-right-of-way"
          >
            Right-of-Way
          </TabsTrigger>
          <TabsTrigger
            value="jurisdictions"
            className="px-3 text-xs font-normal data-[state=active]:font-medium"
            data-testid="intelligence-tab-jurisdictions"
          >
            Jurisdictions
          </TabsTrigger>
          <TabsTrigger
            value="infrastructure"
            className="px-3 text-xs font-normal data-[state=active]:font-medium"
            data-testid="intelligence-tab-infrastructure"
          >
            Infrastructure
          </TabsTrigger>
        </TabsList>
        <TabsContent value="conflicts">
          <Conflicts />
        </TabsContent>
        <TabsContent value="right-of-way" className="py-3">
          <RightOfWay />
        </TabsContent>
        <TabsContent value="jurisdictions" className="py-3">
          <Jurisdiciton />
        </TabsContent>
        <TabsContent value="infrastructure" className="py-3">
          <Infrastructure />
        </TabsContent>
      </Tabs>
    </Fragment>
  );
}
